import { NavLink } from 'react-router';
import { motion } from 'framer-motion';
import { SPRING_STATUS, TWEEN_FAST } from '../../lib/motion';
import type { LucideIcon } from 'lucide-react';
import { cn } from '../../lib/cn';

interface NavItem {
  label: string;
  to: string;
  icon: LucideIcon;
  badge?: number;
  end?: boolean;
}

interface SidebarSectionProps {
  title: string;
  items: NavItem[];
  onItemClick?: () => void;
}

// Framer-motion-enhanced NavLink — gives us whileHover / whileTap on an <a> tag
const MotionNavLink = motion.create(NavLink);

export function SidebarSection({ title, items, onItemClick }: SidebarSectionProps) {
  if (items.length === 0) return null;

  return (
    <div>
      <p className="px-3 mb-2 font-mono text-[11px] font-semibold text-muted/70 uppercase tracking-wider">
        {title}
      </p>
      <ul className="space-y-0.5">
        {items.map((item) => (
          <li key={item.to}>
            <MotionNavLink
              to={item.to}
              end={item.end ?? item.to === '/'}
              onClick={onItemClick}
              whileTap={{ scale: 0.98 }}
              transition={TWEEN_FAST}
              className={({ isActive }: { isActive: boolean }) =>
                cn(
                  'flex items-center gap-3 px-3 py-2.5 text-sm rounded-lg relative overflow-hidden',
                  isActive
                    ? 'bg-primary/10 text-text'
                    : 'text-muted hover:text-text hover:bg-white/[0.05]',
                )
              }
            >
              {({ isActive }: { isActive: boolean }) => (
                <>
                  {/* Animated left border indicator that slides between active items */}
                  {isActive && (
                    <motion.span
                      layoutId="activeNav"
                      className="absolute left-0 top-0 bottom-0 w-[3px] rounded-r-full bg-primary"
                      transition={SPRING_STATUS}
                    />
                  )}

                  <item.icon className="w-4 h-4 relative z-10" />
                  <span className="flex-1 relative z-10">{item.label}</span>
                  {item.badge !== undefined && item.badge > 0 && (
                    <span className="bg-danger text-white text-[10px] font-mono font-bold rounded-full min-w-[20px] h-5 flex items-center justify-center px-1.5 relative z-10">
                      {item.badge > 99 ? '99+' : item.badge}
                    </span>
                  )}
                </>
              )}
            </MotionNavLink>
          </li>
        ))}
      </ul>
    </div>
  );
}
